import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Formik, Form } from 'formik'
import { Box, Text } from 'rebass'
import * as yup from 'yup'
import { useNewTranslation } from 'hooks/index'
import Button from 'components/Shared/Button/OldButton'
import Inputs from 'components/Inputs'
import { SUBMISSION_VIEW_ENUM } from 'state/submissions'
import { GroupedOption, TagOption, selectRejectionTagGroupOptions } from 'state/constants'
import { theme } from 'theme'
import {
    REVIEW_MODAL_TYPE,
    selectIsMultiSelectMode,
    selectSelectedSubmissionView,
    setReviewModalOpen,
    setReviewModalType,
    setRejectPopupSubmissionUuid,
    setRejectPopupSubmissionProgressUuid,
    selectCheckCanReviewSubmissionByStep,
    VERIFY_TAB_TYPE,
} from 'state/review'
import { StepState } from 'state/tasks'
import { SubmissionActionWrapper } from './styles'
import RectangleButton from 'particles/Button/RectangleButton'
import { SEARCH_PARAMS, getSearchParams } from 'components/utils/url'
import { useHistory } from 'react-router'

interface NoneApproveFormValues {
    comment: string | null
    tag_uuids: TagOption[]
}

interface DisplayGroupedOption extends GroupedOption {
    isDisabled?: boolean
}

interface RejectionFromProps {
    handleCancel: any
    handleReject: any
    handleRejectTagChange: any
}

const RejectionForm: React.FC<RejectionFromProps> = ({ handleCancel, handleReject, handleRejectTagChange }) => {
    const [newT] = useNewTranslation()
    const rejectionTagGroupOptions = useSelector(selectRejectionTagGroupOptions())
    const [submissionRejectTagGroupOptions, setSubmissionRejectTagGroupOptions] =
        useState<DisplayGroupedOption[]>(rejectionTagGroupOptions)
    const [formProps, setFormProps] = useState<NoneApproveFormValues>({
        comment: '',
        tag_uuids: [],
    })
    const validation = yup.object({
        comment: yup
            .string()
            .nullable()
            .max(10000, ({ max }) =>
                newT(
                    'errorMessage.validation.enterLessThanCharacterLimit',
                    'Please enter less than {{max}} characters.',
                    {
                        max,
                    },
                ),
            )
            .required(
                newT('errorMessage.validation.enterRequestEditReason', 'Please enter a reason for the Partiposter'),
            ),
        tag_uuids: yup
            .array()
            .nullable()
            .required(
                newT('errorMessage.validation.selectRequestEditReason', 'Please select a reason for edits above.'),
            ),
    })
    const onTagChange = (options: any) => {
        handleRejectTagChange(options, setSubmissionRejectTagGroupOptions, setFormProps)
    }
    return (
        <Formik
            initialValues={{ comment: formProps.comment, tag_uuids: formProps.tag_uuids }}
            enableReinitialize={true}
            validationSchema={validation}
            onSubmit={(values: { comment: string | null; tag_uuids: TagOption[] }, actions) => {
                const retTagUuids = values.tag_uuids.map((tag: TagOption) => tag.uuid)
                handleReject(values.comment, retTagUuids)
                actions.setSubmitting(false)
            }}
        >
            {() => (
                <Form>
                    <Box width="100%" sx={{ textAlign: 'left' }}>
                        <Text fontSize="1.2em" textAlign="center">
                            {newT(
                                'modalWindow.reasonForPartiposterRejectionTitle',
                                "The reason for this Partiposter's rejection is:",
                            )}
                        </Text>
                        <Text color={theme.colors.doveGrey} fontSize="0.6em" textAlign="center" ml={theme.spacing.xxs}>
                            {newT(
                                'submission.review.reasonsForRejectionSubtitle',
                                'Select up to 4 only. Removing or adding reasons will reset the comments below.',
                            )}
                        </Text>
                        <Inputs.Dropdown
                            name="tag_uuids"
                            options={submissionRejectTagGroupOptions}
                            isClearable
                            isSearchable
                            isMulti
                            placeHolder={newT('placeholderText.clickToSelect')}
                            onSelect={onTagChange}
                            defaultSelected={formProps.tag_uuids}
                        />
                        <Inputs.TextArea
                            name="comment"
                            placeholder={newT('submission.review.reasonsForRequestEditSubtitle')}
                            minTextAreaHeight={80}
                            width="98%"
                            isDisabled={formProps.tag_uuids && formProps.tag_uuids.length == 0}
                        />
                        <Box width="90%" sx={{ margin: 'auto' }}>
                            <Text color={theme.colors.boulder} fontSize="0.75em" textAlign="center">
                                {newT(
                                    'submission.review.reviewRejectionComment',
                                    'We will be reviewing this rejection before the Partiposter is notified.',
                                )}
                            </Text>
                            <Text
                                color={theme.colors.boulder}
                                fontSize="0.75em"
                                textAlign="center"
                                mt={theme.spacing.xxxs}
                            >
                                {newT(
                                    'submission.review.reviewRejectionInstruction',
                                    'Once rejected, the Partiposter will be banned from resubmitting content to the campaign.',
                                )}
                            </Text>
                        </Box>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Button width="50%" onClick={handleCancel}>
                            {newT('button.cancel')}
                        </Button>
                        <Button
                            width="50%"
                            type="submit"
                            bg={theme.colors.monza}
                            sx={{ borderRadius: '0.8em' }}
                            my={theme.spacing.xs}
                            textProps={{ color: theme.newColors.neutral50, fontSize: '0.8em', px: theme.spacing.s }}
                            disabled={formProps.tag_uuids && formProps.tag_uuids.length == 0}
                            opacity={formProps.tag_uuids && formProps.tag_uuids.length == 0 ? 0.4 : 1}
                        >
                            {newT('button.reject')}
                        </Button>
                    </Box>
                </Form>
            )}
        </Formik>
    )
}

interface RejectOrVerifyProps {
    submissionUuid?: string
    submissionProgressUuid: string
    step: StepState
}

const RejectOrVerify: React.FC<RejectOrVerifyProps> = ({ submissionUuid, submissionProgressUuid, step }) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const history = useHistory()

    const isMultiSelectMode = useSelector(selectIsMultiSelectMode)
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const checkCanReviewSubmissionByStep = useSelector(selectCheckCanReviewSubmissionByStep)
    const canRejectOrVerify = useMemo(
        () => checkCanReviewSubmissionByStep(step.type),
        [checkCanReviewSubmissionByStep, step.type],
    )

    const handleVerify = () => {
        const searchParams = getSearchParams({
            add: [
                { key: SEARCH_PARAMS.reviewId, value: submissionUuid ? submissionUuid : submissionProgressUuid },
                { key: SEARCH_PARAMS.verifyTab, value: VERIFY_TAB_TYPE.REVIEW },
            ],
        })
        history.push(searchParams)
    }

    const handleModalPopup = () => {
        if (submissionUuid && selectedSubmissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS) {
            dispatch(setRejectPopupSubmissionUuid(submissionUuid))
        }
        dispatch(setRejectPopupSubmissionProgressUuid(submissionProgressUuid))
        dispatch(setReviewModalType(REVIEW_MODAL_TYPE.SINGLE_REJECT))
        dispatch(setReviewModalOpen(true))
    }

    if (
        selectedSubmissionView !== SUBMISSION_VIEW_ENUM.PENDING &&
        selectedSubmissionView !== SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS
    ) {
        return null
    }

    return canRejectOrVerify ? (
        <SubmissionActionWrapper>
            <RectangleButton
                form="filled"
                size="m"
                label={newT('button.verify')}
                onClick={handleVerify}
                isDisabled={isMultiSelectMode}
                className="width-full"
            />
            <RectangleButton
                form="outlined"
                size="m"
                color="neutral"
                label={newT('button.reject', 'Reject')}
                onClick={handleModalPopup}
                isDisabled={isMultiSelectMode}
                className="width-full"
            />
        </SubmissionActionWrapper>
    ) : null
}

export { RejectionForm, RejectOrVerify }
